import { CircleAlert, CircleCheck } from "lucide-react";

import type { CustomEnemySummary } from "../../api/types";
import { ProgressBar } from "../../components/Gauge";
import { Motion } from "../../components/presence";
import { Waiting } from "../../components/Waiting";
import { t, tServer } from "../../i18n/i18n";
import { PREPARING, REFINING } from "./sheet";

/** État du modèle de l'ENI (registre A135, amendement du 28/10/2026) : « Préparation du modèle de l'ENI… » (points
 *  animés, quelques minutes) → « Modèle prêt à l'essai » → « Affinage en cours (quand le serveur est disponible) »
 *  avec son avancement → « Modèle complet prêt », ou l'échec. Un brouillon dit ce que la validation déclenche. L'ENI
 *  est utilisable dès sa validation, modèle prêt ou non. Chaque état entre en fondu. */
export function TrainingState({ enemy }: { enemy: CustomEnemySummary }) {
  const training = enemy.training;
  const status = training.status;
  const percent = typeof training.percent === "number" ? training.percent : null;
  let body;
  if (training.enabled === false) {
    // Entraînement coupé dans cet environnement (production) : le modèle d'IA habituel rédige d'après la fiche.
    body = (
      <span>
        {enemy.status === "draft"
          ? t("Brouillon : à valider pour l'employer dans vos dossiers.")
          : t("Rédigé par le modèle d'IA habituel, d'après sa fiche")}
      </span>
    );
  } else if (enemy.status === "draft") {
    body = <span>{t("Brouillon : son modèle sera préparé dès sa validation.")}</span>;
  } else if (PREPARING.includes(status)) {
    body = (
      <span>
        <Waiting>{t("Préparation du modèle de l'ENI…")}</Waiting> {t("Quelques minutes ; l'ENI est déjà utilisable.")}
      </span>
    );
  } else if (REFINING.includes(status)) {
    body = (
      <span className="block space-y-1">
        <span className="flex items-center gap-1.5">
          <CircleCheck size={14} className="shrink-0 text-ok" aria-hidden /> {t("Modèle prêt à l'essai")}
        </span>
        <span className="block">{t("Affinage en cours (quand le serveur est disponible)")}</span>
        {percent !== null && <ProgressBar value={percent / 100} tone="run" label={t("Avancement de l'affinage")} className="max-w-60" />}
      </span>
    );
  } else if (status === "quick_ready" || status === "ready") {
    body = (
      <span className="flex items-center gap-1.5">
        <CircleCheck size={14} className="shrink-0 text-ok" aria-hidden />
        {status === "ready" ? t("Modèle complet prêt") : t("Modèle prêt à l'essai")}
      </span>
    );
  } else if (status === "failed") {
    body = (
      <span className="flex items-start gap-1.5 text-danger">
        <CircleAlert size={14} className="mt-0.5 shrink-0" aria-hidden />
        <span>
          {t("Échec de la préparation du modèle de l'ENI")}
          {training.error ? ` : ${tServer(training.error)}` : ""}
          {training.model_ready ? ` ${t("Le modèle précédent reste en service.")}` : ""}
        </span>
      </span>
    );
  } else {
    body = <span>{t("Modèle de l'ENI non préparé")}</span>;
  }
  return (
    <Motion key={`${enemy.status}-${status}`} origin="fade" className="mt-0.5 block text-meta leading-snug text-ink-muted" data-testid="custom-enemy-training">
      {body}
    </Motion>
  );
}
